import dayjs, { type Dayjs } from 'dayjs'
import { DayPicker, type Matcher } from 'react-day-picker'

import { getTranslate } from '@/legacy-src/utilities/i18n'
import { capitalize } from '@/legacy-src/utilities/textUtilities'

import { formatMonthYear } from '@/src/utils/formatLocalizedDate'

import { ClientBookingMonthCalendarCaption } from './ClientBookingMonthCalendarCaption'
import { ClientBookingMonthCalendarChevron } from './ClientBookingMonthCalendarChevron'
import { ClientBookingMonthCalendarDayButton } from './ClientBookingMonthCalendarDayButton'
import { ClientBookingMonthCalendarNavButton } from './ClientBookingMonthCalendarNavButton'

export interface ClientBookingMonthCalendarProps {
  month: Dayjs
  onMonthChange: (month: Dayjs) => void
  day: Dayjs
  onDaySelect: (day: Dayjs) => void
  disabled: Matcher
  hasAppointment?: Matcher
  hasSelectedSlot?: Matcher
}

export const ClientBookingMonthCalendar = ({
  month,
  onMonthChange,
  day,
  onDaySelect,
  disabled,
  hasAppointment,
  hasSelectedSlot,
}: ClientBookingMonthCalendarProps) => {
  const { t } = getTranslate()
  const currentMonth = dayjs().startOf('month')

  return (
    <DayPicker
      mode="single"
      required
      selected={day.toDate()}
      onSelect={(date) => onDaySelect(dayjs(date))}
      month={month.toDate()}
      onMonthChange={(date) => onMonthChange(dayjs(date))}
      startMonth={currentMonth.toDate()}
      weekStartsOn={1}
      navLayout="around"
      modifiers={{ disabled, hasAppointment, hasSelectedSlot }}
      components={{
        DayButton: ClientBookingMonthCalendarDayButton,
        MonthCaption: ClientBookingMonthCalendarCaption,
        PreviousMonthButton: ClientBookingMonthCalendarNavButton,
        NextMonthButton: ClientBookingMonthCalendarNavButton,
        Chevron: ClientBookingMonthCalendarChevron,
      }}
      classNames={{
        month: 'grid grid-cols-7 gap-y-6',
        button_previous: 'justify-self-center',
        month_caption: 'col-span-5',
        button_next: 'justify-self-center',
        month_grid: 'col-span-7 w-full',
        day: 'laptop:py-2 tablet:py-2 py-0.5 text-center',
        weekday: 'text-center font-normal text-sm pb-4 uppercase',
      }}
      formatters={{
        formatWeekdayName: (date) => dayjs(date).format('dd'),
        formatCaption: (date) => capitalize(formatMonthYear(dayjs(date))),
      }}
      labels={{
        labelWeekday: (date) => capitalize(dayjs(date).format('dddd')),
        labelGrid: (date) => capitalize(formatMonthYear(dayjs(date))),
        labelDayButton: (date) => capitalize(dayjs(date).format('dddd, L')),
        labelPrevious: () => t('component.calendar.previousMonth'),
        labelNext: () => t('component.calendar.nextMonth'),
      }}
    />
  )
}
